import { type ReactNode, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useNavigate, useRouteLoaderData, useSearchParams } from '@remix-run/react';
import type { Recommendation, TRecommendationsElement, TRecommendationsModalElement } from '@shared/dream-components';

import { usePageContext } from '~/context/usePageContext';
import useEmail from '~/hooks/useEmail';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';
import type { loader } from '~/root';
import DataLayer from '~/services/DataLayer';

import ActionForm from '../ActionForm';
import { useComponentFetcher } from '../ComponentErrorBoundary/useComponentFetcher';
import StatusInputs from '../Forms/StatusInputs';
import useFormSubmit from './_hooks/useFormSubmit';

type RenderProps = {
  isLoading: boolean;
  email?: string;
  recommendations: Recommendation[];
  handleChangeInRecommendations: (recommendation: Recommendation) => void;
  handleSelectAllRecommendations: (recommendations: Recommendation[]) => void;
  handleDeselectAllRecommendations: () => void;
  onClickSubmit: (email: string) => boolean;
  onClickCancel: () => void;
};

type Props = {
  node?: TRecommendationsElement | TRecommendationsModalElement;
  children: (props: RenderProps) => ReactNode;
  onSuccess?: () => void;
};

// biome-ignore lint/correctness/noUnusedVariables: Keep this for now
const RecommendationsAction = ({ node, children, onSuccess }: Props) => {
  const { t } = useTranslation();
  const [email] = useEmail();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetcher = useComponentFetcher();
  const { publication } = useRouteLoaderData<typeof loader>('root');
  const { visitToken, referralId, setCookie } = usePageContext();
  const [selectedRecommendations, setSelectedRecommendations] = useState<Recommendation[]>([]);
  const navigate = useNavigate();
  const { nextPage, nextStep } = useSignupRedirects();

  const formRef = useRef<HTMLFormElement>(null);

  const { handleSubmit } = useFormSubmit({
    recommendations: selectedRecommendations,
    fetcher,
    nextPage,
  });

  const getToastMessage = () => {
    if (publication.require_subscriber_approval) {
      return t('notifications.subscribe.require_approval');
    }

    if (publication.double_opt_required) {
      return t('notifications.subscribe.confirm');
    }

    return t('notifications.subscribe.subscribed');
  };

  const handleSuccess = (data: any) => {
    setCookie({ name: 'email_capture_disabled', value: 'true', days: 30 });

    if (data?.dataLayerEvent) {
      DataLayer.pushEvent(data.dataLayerEvent);
    }

    toast.success(getToastMessage());
    formRef.current?.reset();
    setSelectedRecommendations([]);

    if (nextStep) {
      nextPage();
      return;
    }

    if (searchParams.get('modal') === 'recommendations') {
      searchParams.delete('modal');
      setSearchParams(searchParams);
    }
  };

  const handleError = (_data: any) => {
    toast.success(getToastMessage());
  };

  const handleChangeInRecommendations = (recommendation: Recommendation) => {
    const newValues = [...selectedRecommendations];

    const indexOfRecommendation = newValues.findIndex((_recommendation) => _recommendation.id === recommendation.id);

    if (indexOfRecommendation > -1) {
      newValues.splice(indexOfRecommendation, 1);
    } else {
      newValues.push(recommendation);
    }

    setSelectedRecommendations(newValues);
  };

  const handleSelectAllRecommendations = (recommendations: Recommendation[]) => {
    setSelectedRecommendations(recommendations);
  };

  const handleDeselectAllRecommendations = () => {
    setSelectedRecommendations([]);
  };

  const isLoading = fetcher.state === 'submitting';

  const handleClickSubmit = (email: string) => {
    if (formRef.current) {
      const formData = new FormData(formRef.current);
      return handleSubmit(formData, email);
    }

    return false;
  };

  const handleClickCancel = () => {
    if (nextStep) {
      nextPage();
      return;
    }

    // TODO: Uncomment this if we go back to having this redirect as an option
    // if (node?.attrs?.cancelButtonRedirectUrl) {
    //   window.location.href = node.attrs.cancelButtonRedirectUrl;
    //   return;
    // }

    navigate('/');
  };

  return (
    <ActionForm
      fetcher={fetcher}
      formRef={formRef}
      method="post"
      action="/recommendations_subscribe_form"
      onSubmit={(e) => {
        const formData = new FormData(e.currentTarget);
        handleSubmit(formData);
      }}
      onSuccess={(data) => {
        handleSuccess(data);
        setSelectedRecommendations([]);
      }}
      onError={(data) => {
        handleError(data);
      }}
    >
      <StatusInputs name="recommendations" errorMessage={t('errors.default')} successMessage="" />
      {referralId && <input hidden name="ref" defaultValue={referralId} />}
      {visitToken && <input hidden name="visit_token" defaultValue={visitToken} />}
      <input hidden name="publication_id" defaultValue={publication.id} />
      <input name="sent_from_orchid" type="hidden" defaultValue="true" />
      <input name="include_current_publication" type="hidden" defaultValue="false" />
      {children({
        isLoading,
        email,
        recommendations: selectedRecommendations,
        handleChangeInRecommendations,
        onClickSubmit: handleClickSubmit,
        onClickCancel: handleClickCancel,
        handleSelectAllRecommendations,
        handleDeselectAllRecommendations,
      })}
    </ActionForm>
  );
};

export default RecommendationsAction;
